import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import LabelCDS from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS';
import { LabelSize } from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS/LabelCDS.constants';
import PropTypes from 'prop-types';

// Concrete module, not the store/loanApplications/selectors barrel (see WorksheetDrawer's imports).
import { selectApplicationChecklistsFormattedValues } from 'store/loanApplications/selectors/checklists.selectors';

import {
  worksheetStatusBarChipsStyles,
  worksheetStatusBarStyles,
} from '../../WorksheetDrawer.styles';

import { WorksheetStatusBarTestIds } from './WorksheetStatusBar.testIds';
import { buildWorksheetStatusChips, getWorksheetLastSavedLabel } from './WorksheetStatusBar.utils';

/**
 * Pinned to the top of the worksheet: when it was last saved, and the pass/fail of its calculated
 * values and linked checklist.
 */
const WorksheetStatusBar = ({
  lastSavedAt = null,
  isLoading = false,
  calculatedValues = [],
  checklistId = null,
}) => {
  const checklistsResults = useSelector(selectApplicationChecklistsFormattedValues);

  const chips = useMemo(
    () =>
      buildWorksheetStatusChips({
        calculatedValues,
        checklistResult:
          checklistsResults.find(({ checklist }) => checklist?.id === checklistId) ?? null,
      }),
    [calculatedValues, checklistId, checklistsResults],
  );

  return (
    <Box data-testid={WorksheetStatusBarTestIds.BAR} sx={worksheetStatusBarStyles}>
      <Typography
        color="text.secondary"
        component="span"
        data-testid={WorksheetStatusBarTestIds.LAST_SAVED}
        variant="regular14"
      >
        {getWorksheetLastSavedLabel({ lastSavedAt, isLoading })}
      </Typography>

      <Box sx={worksheetStatusBarChipsStyles}>
        {chips.map(({ key, title, variant }) => (
          <Box data-testid={WorksheetStatusBarTestIds.CHIP} key={key}>
            <LabelCDS size={LabelSize.M} title={title} variant={variant} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

WorksheetStatusBar.propTypes = {
  lastSavedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  isLoading: PropTypes.bool,
  calculatedValues: PropTypes.arrayOf(
    PropTypes.shape({
      calculationFieldId: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      value: PropTypes.string,
    }),
  ),
  checklistId: PropTypes.string,
};

export default memo(WorksheetStatusBar);
